import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { 
	Divider,
	Typography,
	Paper,
	Icon, 
	List, 
	ListItem, 
	ListItemText,
	Grid,
	Card,
	CardContent,
	Button,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	CircularProgress,
	Tooltip,
	IconButton,
	Box
} from '@material-ui/core';
import { AccountTree, LocalHospital, Refresh as RefreshIcon } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { FuseAnimateGroup } from '@fuse';
import { makeStyles } from '@material-ui/core/styles';
import GpuUtilizationChart from '../widgets/GpuUtilizationChart';
import GpuMetricsChart from '../widgets/GpuMetricsChart';
import MetricHistoryChart from '../widgets/MetricHistoryChart';
import StressTestPanel from '../widgets/StressTestPanel';
import ClickableMetricCard from '../widgets/ClickableMetricCard';
import TopologyMapScalable from '../widgets/TopologyMapScalable';
import * as colors from '@material-ui/core/colors';
import * as Actions from '../../store/actions';

const useStyles = makeStyles(theme => ({
	listItem: {
		color: 'secondary',
		textDecoration: 'none!important',
		height: 40,
		paddingLeft: 24,
		paddingRight: 12,
		'&.active': {
			backgroundColor: theme.palette.secondary.main,
			color: theme.palette.secondary.contrastText + '!important',
			pointerEvents: 'none',
			'& .list-item-icon': {
				color: 'inherit'
			}
		},
		'& .list-item-icon': {
			marginRight: 16
		}
	},
	selectedItem: {
		color: 'secondary',
		textDecoration: 'none!important',
		height: 40,
		paddingLeft: 24,
		paddingRight: 12,
		backgroundColor: '#f2f2f2',
		'&.active': {
			backgroundColor: theme.palette.secondary.main,
			color: theme.palette.secondary.contrastText + '!important',
			pointerEvents: 'none',
			'& .list-item-icon': {
				color: 'inherit'
			}
		},
		'& .list-item-icon': {
			marginRight: 16
		}
	},
}));

const ObservabilityTab = ({ 
	healthData,
	clusterId,
	nodes,
	cluster
}) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [selectedView, setSelectedView] = useState('topology');

	// Check URL parameters for specific view on mount
	useEffect(() => {
		const hash = window.location.hash;
		if (hash.includes('selectedNode=')) {
			// If there's a selectedNode parameter, switch to GPU view
			setSelectedView('gpu');
		}
	}, []);

	// Listen for hash changes to update view
	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash;
			if (hash.includes('selectedNode=')) {
				setSelectedView('gpu');
			}
		};

		window.addEventListener('hashchange', handleHashChange);
		return () => window.removeEventListener('hashchange', handleHashChange);
	}, []);
	
	// State for performance overview
	const [selectedOverviewNode, setSelectedOverviewNode] = useState('all');
	const [selectedTimeRange, setSelectedTimeRange] = useState(24);
	const [lastUpdated, setLastUpdated] = useState(null);
	
	// Get cluster metrics data from Redux store
	const { 
		data: clusterMetrics, 
		history: metricsHistory,
		loading: metricsLoading, 
		error: metricsError 
	} = useSelector(({ clusters }) => clusters.cluster.clusterMetrics);

	const timeRangeOptions = [
		{ value: 1, label: '1 Hour' },
		{ value: 6, label: '6 Hours' },
		{ value: 24, label: '24 Hours' },
		{ value: 168, label: '1 Week' }
	];

	// Fetch latest metrics
	const fetchLatestMetrics = async () => {
		if (!clusterId) return;
		try {
			const result = await dispatch(Actions.fetchClusterMetrics(clusterId));
			setLastUpdated(new Date());
		} catch (err) {
			console.error('Error fetching metrics:', err);
		}
	};

	// Get available nodes from cluster metrics
	const getAvailableNodes = () => {
		
		if (!clusterMetrics) {
			return [];
		}
		
		// Check different possible data structure locations
		let nodeMetrics = null;
		if (clusterMetrics.nodeMetrics) {
			nodeMetrics = clusterMetrics.nodeMetrics;
		} else if (clusterMetrics.nodes) {
			nodeMetrics = clusterMetrics.nodes;
		} else if (clusterMetrics.data?.nodeMetrics) {
			nodeMetrics = clusterMetrics.data.nodeMetrics;
		} else if (clusterMetrics.data?.nodes) {
			nodeMetrics = clusterMetrics.data.nodes;
		} else {
			return [];
		}
		
		const hostnames = [...new Set(nodeMetrics.map(node => {
			const hostname = node.hostname || node.nodeName || node.name || node.host;
			return hostname;
		}).filter(Boolean))].sort();
		
		return hostnames;
	};

	// Calculate metrics for specific node or all nodes
	const getDisplayMetrics = () => {
		
		if (!clusterMetrics?.summary) {
			return null;
		}
		
		if (selectedOverviewNode === 'all') {
			return clusterMetrics.summary;
		}
		
		// Calculate metrics for specific node
		let nodeMetrics = null;
		if (clusterMetrics.nodeMetrics) {
			nodeMetrics = clusterMetrics.nodeMetrics;
		} else if (clusterMetrics.nodes) {
			nodeMetrics = clusterMetrics.nodes;
		} else if (clusterMetrics.data?.nodeMetrics) {
			nodeMetrics = clusterMetrics.data.nodeMetrics;
		} else if (clusterMetrics.data?.nodes) {
			nodeMetrics = clusterMetrics.data.nodes;
		}
		
		if (!nodeMetrics) {
			return clusterMetrics.summary;
		}
		
		const nodeData = nodeMetrics.find(node => {
			const hostname = node.hostname || node.nodeName || node.name || node.host;
			return hostname === selectedOverviewNode;
		});
				
		if (!nodeData) {
			return clusterMetrics.summary;
		}
		
		// Return node-specific metrics
		const metrics = {
			avgCpuLoad: nodeData.system?.loadAverage?.['1min'] || 0,
			avgMemoryUsage: nodeData.system?.memory?.usagePercent || 0,
			avgDiskUsage: nodeData.system?.disk?.usagePercent || 0,
			totalIOPS: nodeData.system?.disk?.totalIOPS || 0,
			avgNetworkIO: nodeData.network ? 
				((nodeData.network.totalTransmitted || 0) + (nodeData.network.totalReceived || 0)) / (1024 * 1024) : 0,
			avgGpuUtilization: nodeData.gpu?.utilization || 0,
			totalNodes: 1, // Single node
			activeGpus: nodeData.gpu?.count || (nodeData.gpu ? 1 : 0),
			totalMemoryGB: nodeData.system?.memory?.total ? 
				Math.round((nodeData.system.memory.total / (1024 * 1024 * 1024)) * 10) / 10 : 0,
			totalStorageGB: nodeData.system?.disk?.total ? 
				Math.round((nodeData.system.disk.total / (1024 * 1024 * 1024)) * 10) / 10 : 0
		};
		
		return metrics;
	};

	// Fetch historical data
	const fetchHistoricalData = async () => {
		if (!clusterId) return;
		try {
			await dispatch(Actions.fetchClusterMetricsHistory(clusterId, selectedTimeRange, 'all'));
		} catch (err) {
			console.error('Error fetching historical data:', err);
		}
	};

	// Force metrics collection
	const forceCollection = async () => {
		if (!clusterId) return;
		try {
			await dispatch(Actions.forceClusterMetricsCollection(clusterId));
			setLastUpdated(new Date());
		} catch (err) {
			console.error('Error forcing metrics collection:', err);
		}
	};

	useEffect(() => {
		if (clusterId && selectedView === 'performance') {
			fetchLatestMetrics();
			fetchHistoricalData();
		}
	}, [clusterId, selectedView, selectedTimeRange]);

	// HPC Observability menu items
	const observabilityMenuItems = [
		{
			id: 'topology',
			text: 'Cluster Topology',
			icon: 'device_hub',
			iconColor: '#2196F3', // Blue
		},
		{
			id: 'health',
			text: 'General Metrics',
			icon: 'dashboard',
			iconColor: '#4CAF50', // Green
		},
		{
			id: 'gpu',
			text: 'GPU Metrics',
			icon: 'memory',
			iconColor: '#FF9800', // Orange
		},
		// {
		// 	id: 'workload',
		// 	text: 'Jobs',
		// 	icon: 'bar_chart',
		// 	iconColor: 'text-indigo',
		// 	description: 'Job performance and resource usage'
		// },
		// {
		// 	id: 'network',
		// 	text: 'Net',
		// 	icon: 'network_check',
		// 	iconColor: 'text-cyan',
		// 	description: 'InfiniBand and network performance'
		// },
		{
			id: 'performance',
			text: 'Performance Overview',
			icon: 'assessment',
			iconColor: '#673AB7', // Deep Purple
		},
		{
			id: 'storage',
			text: 'I/O Performance',
			icon: 'storage',
			iconColor: '#795548', // Brown
		},
		{
			id: 'stress',
			text: 'Stress Testing',
			icon: 'flash_on',
			iconColor: '#673AB7', // Deep Purple
		},
		// {
		// 	id: 'thermal',
		// 	text: 'Temp',
		// 	icon: 'thermostat',
		// 	iconColor: 'text-red',
		// 	description: 'Temperature and cooling metrics'
		// }
	];

	const getHealthStatusColor = (status) => {
		switch (status) {
			case 'healthy': return 'text-green';
			case 'degraded': return 'text-orange';
			case 'offline': return 'text-red';
			default: return 'text-grey';
		}
	};

	const getHealthStatusIcon = (status) => {
		switch (status) {
			case 'healthy': return 'check_circle';
			case 'degraded': return 'warning';
			case 'offline': return 'error';
			default: return 'help';
		}
	};

	const formatBytes = (bytes) => {
		if (!bytes || bytes === 0) return '0 GB';
		const gb = bytes / (1024 * 1024 * 1024);
		return `${gb.toFixed(1)} GB`;
	};

	const formatPercentage = (value) => {
		if (value === null || value === undefined) return 'N/A';
		return `${value.toFixed(1)}%`;
	};

	const renderContent = () => {
		switch (selectedView) {
			case 'topology':
				return (
					<div className="w-full">
						<div className="flex items-center mb-16">
							<AccountTree className="text-blue text-24 mr-12" />
							<Typography className="text-20 font-bold" color="textPrimary">
								HPC Infrastructure Topology
							</Typography>
						</div>
						<Typography className="text-14 mb-24" color="textSecondary">
							Interactive visualization of compute nodes, storage systems, and network fabric.
						</Typography>
						<TopologyMapScalable 
							nodes={nodes || []}
							cluster={cluster}
							healthData={healthData}
							className="w-full"
						/>
					</div>
				);
			case 'health':
				return renderHealthMetrics();
			case 'gpu':
				return renderGpuMetrics();
			case 'performance':
				return renderPerformanceOverview();
			case 'storage':
				return renderIoPerformanceMetrics();
			case 'stress':
				return renderStressTestPanel();
			default:
				return (
					<div className="w-full">
						<div className="flex items-center mb-16">
							<Icon className={`text-24 mr-12 ${observabilityMenuItems.find(item => item.id === selectedView)?.iconColor || 'text-grey'}`}>
								{observabilityMenuItems.find(item => item.id === selectedView)?.icon || 'help'}
							</Icon>
							<Typography className="text-20 font-bold" color="textPrimary">
								{observabilityMenuItems.find(item => item.id === selectedView)?.text || 'Feature'}
							</Typography>
						</div>
						<Typography className="text-14 mb-24" color="textSecondary">
							{observabilityMenuItems.find(item => item.id === selectedView)?.description || 'Feature description'}
						</Typography>
						<Paper className="p-24">
							<Typography variant="h6">Coming Soon</Typography>
							<Typography variant="body2" color="textSecondary">
								This observability feature is under development and will be available in a future release.
							</Typography>
						</Paper>
					</div>
				);
		}
	};

	const renderHealthMetrics = () => (
		<div className="w-full">
			<div className="flex items-center mb-16">
				<LocalHospital className="text-green text-24 mr-12" />
				<Typography className="text-20 font-bold" color="textPrimary">
					System Health Monitoring
				</Typography>
			</div>
			<Typography className="text-14 mb-24" color="textSecondary">
				Real-time health status and system diagnostics
			</Typography>
			
			{/* Health Statistics Card */}
			<Paper className="widget flex flex-col w-full p-24 rounded-8 mb-24" elevation={1}>
				<div className="flex items-center mb-24">
					<Typography className="text-20 font-bold" color="textPrimary">
						Head Node Health Status
					</Typography>
					{healthData && (
						<div className="flex items-center ml-16">
							<Icon className={`text-16 mr-8 ${getHealthStatusColor(healthData.status)}`}>
								{getHealthStatusIcon(healthData.status)}
							</Icon>
							<Typography className={`text-14 font-semibold ${getHealthStatusColor(healthData.status)}`}>
								{healthData.status?.toUpperCase()}
							</Typography>
						</div>
					)}
				</div>
				
				{healthData ? (
					<>
						<div className="flex flex-col lg:flex-row gap-16 justify-between">
							{/* CPU Load Widget */}
							<div className="flex flex-col items-center justify-start py-24 px-16 bg-gray-50 rounded-8 flex-1">
								<Icon className="text-blue text-48 mb-12">memory</Icon>
								<Typography className="text-32 font-bold mb-8" color="textPrimary">
									{healthData.system?.loadAverage?.['1min']?.toFixed(2) || 'N/A'}
								</Typography>
								<Typography className="text-14 text-center" color="textSecondary">
									Load Average (1m)
								</Typography>
								<div className="h-20"></div>
							</div>

							{/* Memory Usage Widget */}
							<div className="flex flex-col items-center justify-start py-24 px-16 bg-gray-50 rounded-8 flex-1">
								<Icon className="text-blue text-48 mb-12">storage</Icon>
								<Typography className="text-32 font-bold mb-8" color="textPrimary">
									{healthData.system?.memory?.usagePercent?.toFixed(1) || 'N/A'}%
								</Typography>
								<Typography className="text-14 text-center" color="textSecondary">
									Memory Usage
								</Typography>
								<Typography className="text-12 mt-2" color="textSecondary">
									{formatBytes(healthData.system?.memory?.used)} / {formatBytes(healthData.system?.memory?.total)}
								</Typography>
							</div>

							{/* Disk Usage Widget */}
							<div className="flex flex-col items-center justify-start py-24 px-16 bg-gray-50 rounded-8 flex-1">
								<Icon className="text-blue text-48 mb-12">folder</Icon>
								<Typography className="text-32 font-bold mb-8" color="textPrimary">
									{healthData.system?.disk?.usagePercent || 'N/A'}%
								</Typography>
								<Typography className="text-14 text-center" color="textSecondary">
									Disk Usage
								</Typography>
								<div className="h-20"></div>
							</div>

							{/* Response Time Widget */}
							<div className="flex flex-col items-center justify-start py-24 px-16 bg-gray-50 rounded-8 flex-1">
								<Icon className="text-blue text-48 mb-12">timer</Icon>
								<Typography className="text-32 font-bold mb-8" color="textPrimary">
									{healthData.responseTime || 'N/A'}
								</Typography>
								<Typography className="text-14 text-center" color="textSecondary">
									Response Time (ms)
								</Typography>
								<div className="h-20"></div>
							</div>
						</div>

						{/* GPU Information Section */}
						{healthData?.system?.gpu?.available && (
							<>
								<div className="flex justify-center mt-24 mb-16">
									<Typography className="text-16 font-semibold" color="textPrimary">
										GPU Information ({healthData.system.gpu.count} GPU{healthData.system.gpu.count > 1 ? 's' : ''})
									</Typography>
								</div>
								<div className="flex flex-col lg:flex-row gap-16 justify-between">
									{healthData.system.gpu.gpus?.map((gpu, index) => (
										<div key={gpu.index} className="flex flex-col items-center justify-start py-24 px-16 bg-gradient-to-br from-green-50 to-blue-50 rounded-8 flex-1">
											<Icon className="text-green text-48 mb-12">memory</Icon>
											<Typography className="text-24 font-bold mb-4" color="textPrimary">
												{gpu.utilizationPercent !== null && gpu.utilizationPercent !== undefined ? gpu.utilizationPercent : 'N/A'}%
											</Typography>
											<Typography className="text-12 text-center mb-2" color="textSecondary">
												GPU {gpu.index} Utilization
											</Typography>
											<Typography className="text-10 text-center mb-2" color="textSecondary">
												{gpu.name}
											</Typography>
											<Typography className="text-10 text-center mb-2" color="textSecondary">
												Temp: {gpu.temperatureC !== null && gpu.temperatureC !== undefined ? gpu.temperatureC : 'N/A'}°C
											</Typography>
											<Typography className="text-10 text-center" color="textSecondary">
												VRAM: {gpu.memory?.usagePercent !== null && gpu.memory?.usagePercent !== undefined ? gpu.memory.usagePercent : 'N/A'}% ({(gpu.memory?.usedMB || 0)} / {(gpu.memory?.totalMB || 0)} MB)
											</Typography>
										</div>
									))}
								</div>
							</>
						)}
						
						<div className="flex justify-center mt-24">
							<Typography className="text-12" color="textSecondary">
								Last checked: {moment(healthData.timestamp).fromNow()}
							</Typography>
						</div>
						
						{healthData.version && (
							<div className="flex justify-center mt-8">
								<Typography className="text-10" color="textSecondary">
									Cluster API version: {healthData.version}
								</Typography>
							</div>
						)}
					</>
				) : (
					<div className="flex flex-col items-center justify-center py-48 px-32">
						<Icon className="text-blue text-64 mb-16">health_and_safety</Icon>
						<Typography className="text-18" color="textSecondary">
							No health data available
						</Typography>
					</div>
				)}
			</Paper>

			{/* GPU Utilization Chart */}
			{healthData?.system?.gpu?.available && clusterId && (
				<GpuUtilizationChart 
					clusterId={clusterId}
					className="widget w-full mt-16 rounded-8"
				/>
			)}
		</div>
	);

	const renderGpuMetrics = () => (
		<div className="w-full" data-gpu-metrics>
			<div className="flex items-center mb-16">
				<Icon className="text-orange text-24 mr-12">memory</Icon>
				<Typography className="text-20 font-bold" color="textPrimary">
					GPU Performance Analytics
				</Typography>
			</div>
			<Typography className="text-14 mb-24" color="textSecondary">
				Real-time GPU monitoring, utilization trends, and performance analytics across all cluster nodes
			</Typography>
			
			
			{/* Enhanced GPU Performance Chart with Comprehensive Metrics */}
			<GpuMetricsChart 
				clusterId={clusterId}
				className="widget w-full mb-24 rounded-8"
			/>
		</div>
	);

	const renderIoPerformanceMetrics = () => (
		<div className="w-full">
			<div className="flex items-center mb-16">
				<Icon className="text-brown text-24 mr-12">storage</Icon>
				<Typography className="text-20 font-bold" color="textPrimary">
					I/O Performance Analytics
				</Typography>
			</div>
			<Typography className="text-14 mb-24" color="textSecondary">
				Real-time storage I/O monitoring, bandwidth analysis, and IOPS tracking across cluster nodes
			</Typography>
			
			{/* I/O Performance Chart */}
			<MetricHistoryChart 
				clusterId={clusterId}
				metricType="iops"
				title="I/O Performance (IOPS)"
				unit="IOPS"
				className="w-full mb-24"
			/>
		</div>
	);
	const renderStressTestPanel = () => (
		<div className="w-full">
			<div className="flex items-center mb-16">
				<Icon className="text-purple text-24 mr-12">flash_on</Icon>
				<Typography className="text-20 font-bold" color="textPrimary">
					HPC Stress Testing Suite
				</Typography>
			</div>
			<Typography className="text-14 mb-24" color="textSecondary">
				Execute stress tests to validate GPU compute performance, storage IOPS, memory bandwidth, and network throughput
			</Typography>
			
			{/* Stress Test Panel */}
			<StressTestPanel 
				clusterId={clusterId}
				nodes={nodes || []}
				className="w-full"
			/>
		</div>
	);

	const renderPerformanceOverview = () => {
		const renderMetricCard = (title, value, unit, icon, color, metricType = null) => {
			// Create a mapping for color names to Material-UI color objects
			const colorMap = {
				blue: colors.blue,
				green: colors.green,
				orange: colors.orange,
				purple: colors.purple,
				red: colors.red,
				indigo: colors.indigo,
				teal: colors.teal,
				cyan: colors.cyan,
				brown: colors.brown,
				deep_orange: colors.deepOrange
			};
			
			const colorObject = colorMap[color] || colors.blue; // fallback to blue if color not found
			
			const cardContent = (
				<Card variant="outlined" className="h-full">
					<CardContent className="p-16 text-center">
						<Box className="flex items-center justify-center mb-8">
							<Icon style={{ color: colorObject[500], fontSize: 24 }}>
								{icon}
							</Icon>
						</Box>
						<Typography variant="h6" className="font-bold mb-4" style={{ color: colorObject[500] }}>
							{value}{unit}
						</Typography>
						<Typography variant="caption" color="textSecondary">
							{title}
						</Typography>
					</CardContent>
				</Card>
			);

			// If metricType is provided, wrap in ClickableMetricCard
			if (metricType && clusterId) {
				return (
					<ClickableMetricCard
						clusterId={clusterId}
						metricType={metricType}
						title={title}
						value={value}
						unit={unit}
						preSelectedNode={selectedOverviewNode !== 'all' ? selectedOverviewNode : null}
					>
						{cardContent}
					</ClickableMetricCard>
				);
			}

			// Otherwise return regular card
			return cardContent;
		};
		
		const formatNumber = (num) => {
			if (!num) return '0';
			if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
			if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
			// Round decimals to 1 decimal place for better readability
			return Number(num).toFixed(1);
		};

		return (
			<div className="w-full">
				<div className="flex items-center justify-between mb-16">
					<div className="flex items-center">
						<Icon className="text-blue text-24 mr-12">assessment</Icon>
						<Typography className="text-20 font-bold" color="textPrimary">
							Cluster Performance Overview
						</Typography>
					</div>
					<div className="flex items-center space-x-12">
						<FormControl size="small" style={{ minWidth: 120 }}>
							<InputLabel>Node</InputLabel>
							<Select
								value={selectedOverviewNode}
								onChange={(e) => setSelectedOverviewNode(e.target.value)}
								label="Node"
							>
								<MenuItem value="all">All Nodes</MenuItem>
								{getAvailableNodes().map(hostname => (
									<MenuItem key={hostname} value={hostname}>
										{hostname}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Tooltip title="Force metrics collection">
							<span>
								<IconButton onClick={forceCollection} disabled={metricsLoading}>
									<RefreshIcon />
								</IconButton>
							</span>
						</Tooltip>
					</div>
				</div>
				<Typography className="text-14 mb-24" color="textSecondary">
					Comprehensive cluster performance metrics, resource utilization, and system analytics
				</Typography>

				{metricsLoading && !clusterMetrics && (
						<div className="flex justify-center items-center p-48">
							<CircularProgress />
						</div>
					)}

					{metricsError && (
						<Paper className="p-24 mb-24 bg-red-50">
							<Typography color="error">
								Error loading performance metrics: {metricsError}
							</Typography>
							<Button 
								onClick={fetchLatestMetrics} 
								color="primary" 
								className="mt-12"
								startIcon={<RefreshIcon />}
							>
								Retry
							</Button>
						</Paper>
					)}

					{/* Current Metrics Summary */}
					{clusterMetrics?.summary && (
						<>
							{(() => {
								const displayMetrics = getDisplayMetrics();
								return (
									<>
										<Typography variant="h6" className="mb-16 font-bold">
											{selectedOverviewNode === 'all' ? 'Current System Status' : `Node ${selectedOverviewNode} Status`}
										</Typography>
										<Grid container spacing={2} className="mb-32">
											<Grid item xs={12} sm={6} md={3}>
												{renderMetricCard(
													'Avg CPU Load',
													displayMetrics.avgCpuLoad?.toFixed(1) || '0',
													'',
													'speed',
													'blue',
													'cpu'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={3}>
												{renderMetricCard(
													'Avg Memory Usage',
													displayMetrics.avgMemoryUsage?.toFixed(1) || '0',
													'%',
													'memory',
													'green',
													'memory'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={3}>
												{renderMetricCard(
													'Avg Disk Usage',
													displayMetrics.avgDiskUsage?.toFixed(1) || '0',
													'%',
													'storage',
													'orange',
													'disk'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={3}>
												{renderMetricCard(
													'Total IOPS',
													formatNumber(displayMetrics.totalIOPS) || '0',
													'',
													'trending_up',
													'purple',
													'iops'
												)}
											</Grid>
										</Grid>
									</>
								);
							})()}
						</>
					)}

					{/* Additional Performance Metrics */}
					{clusterMetrics?.summary && (
						<>
							{(() => {
								const displayMetrics = getDisplayMetrics();
								return (
									<>
										<Typography variant="h6" className="mb-16 font-bold">
											{selectedOverviewNode === 'all' ? 'Resource Summary' : `Node ${selectedOverviewNode} Resource Summary`}
										</Typography>
										<Grid container spacing={2} className="mb-32">
											<Grid item xs={12} sm={6} md={2}>
												{renderMetricCard(
													'Network I/O',
													formatNumber(displayMetrics.avgNetworkIO || 0) || '0',
													' MB/s',
													'network_check',
													'cyan',
													'network'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={2}>
												{renderMetricCard(
													'GPU Utilization',
													(displayMetrics.avgGpuUtilization || 0).toFixed(1) || '0',
													'%',
													'memory',
													'orange',
													'gpu'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={2}>
												{renderMetricCard(
													selectedOverviewNode === 'all' ? 'Active Nodes' : 'Node Status',
													selectedOverviewNode === 'all' ? (displayMetrics.totalNodes || '0') : 'Active',
													'',
													'computer',
													'green',
													'nodes'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={2}>
												{renderMetricCard(
													'Total RAM',
													(displayMetrics.totalMemoryGB !== undefined && displayMetrics.totalMemoryGB !== null) ? formatNumber(displayMetrics.totalMemoryGB) : (displayMetrics.totalNodes * 64).toString(),
													' GB',
													'memory',
													'blue',
													'totalMemory'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={2}>
												{renderMetricCard(
													'Total Storage',
													(displayMetrics.totalStorageGB !== undefined && displayMetrics.totalStorageGB !== null) ? formatNumber(displayMetrics.totalStorageGB) : 'N/A',
													(displayMetrics.totalStorageGB !== undefined && displayMetrics.totalStorageGB !== null) ? ' GB' : '',
													'storage',
													'brown',
													'storage'
												)}
											</Grid>
											<Grid item xs={12} sm={6} md={2}>
												{renderMetricCard(
													'Total GPUs',
													displayMetrics.activeGpus || '0',
													'',
													'memory',
													'deep_orange',
													'totalGpus'
												)}
											</Grid>
										</Grid>
									</>
								);
							})()}
						</>
					)}

					{/* Quick Actions */}
					<Paper className="p-24">
						<Typography variant="h6" className="mb-16 font-bold">
							Performance Actions
						</Typography>
						<div className="flex flex-wrap gap-12">
							<Button 
								variant="outlined" 
								onClick={() => setSelectedView('gpu')}
								startIcon={<Icon>memory</Icon>}
							>
								View GPU Analytics
							</Button>
							<Button 
								variant="outlined" 
								onClick={() => setSelectedView('storage')}
								startIcon={<Icon>storage</Icon>}
							>
								View I/O Performance
							</Button>
							<Button 
								variant="outlined" 
								onClick={() => setSelectedView('stress')}
								startIcon={<Icon>flash_on</Icon>}
							>
								Run Stress Tests
							</Button>
							<Button 
								variant="outlined" 
								onClick={forceCollection}
								disabled={metricsLoading}
								startIcon={<RefreshIcon />}
							>
								Refresh Metrics
							</Button>
						</div>
					</Paper>
				</div>
			);
		};

	return (
		<FuseAnimateGroup enter={{ animation: 'transition.slideUpBigIn' }}>
			<div className="w-full flex flex-row items-start justify-between">
				{/* Side Menu */}
				<div className="p-0 lg:p-24 lg:pr-4" style={{ width: '280px', minWidth: '280px', flexShrink: 0 }}>
					<Paper className="rounded-0 shadow-none lg:rounded-8 lg:shadow-1">
						<div className="flex items-center px-16 h-64 border-b-1">
							<Typography className="text-16 font-bold">Observability</Typography>
						</div>
						<Divider />
						<List>
							{observabilityMenuItems.map(item => (
								<ListItem
									key={item.id}
									id={item.id}
									button
									onClick={() => setSelectedView(item.id)}
									className={`${classes.listItem} ${selectedView === item.id ? classes.selectedItem + ' active' : ''}`}
								>
									<div style={{ 
										display: 'flex', 
										alignItems: 'center', 
										marginRight: '16px',
										color: item.iconColor,
										fontSize: '20px'
									}}>
										<Icon style={{ color: item.iconColor, fontSize: '20px' }}>
											{item.icon}
										</Icon>
									</div>
									<ListItemText 
										className="truncate pr-0 font-bold" 
										primary={item.text} 
										secondary={item.description}
										disableTypography={true} 
									/>
								</ListItem>
							))}
						</List>
					</Paper>
				</div>

				{/* Main Content */}
				<div className="p-16 sm:p-24 w-3/4">
					{renderContent()}
				</div>
			</div>
			
			{/* Last Updated Timestamp at Bottom */}
			{lastUpdated && (
				<div className="w-full flex justify-center pb-16">
					<Typography variant="caption" color="textSecondary" style={{ fontSize: '0.75rem' }}>
						Last updated: {moment(lastUpdated).format('HH:mm:ss')}
					</Typography>
				</div>
			)}
		</FuseAnimateGroup>
	);
};

export default ObservabilityTab;
